import React, { useState, useMemo } from 'react'
import { Link } from '@inertiajs/react'
import { inertiaSave, usePricingReview } from '@/hooks/usePricingReview'
import OperatorLayout from '@/layouts/OperatorLayout'
import PricingRulesNav from '@/components/pricing-rules/PricingRulesNav'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { currencySymbol } from '@/lib/calendar-utils'
import { Shield, Scale, Zap, Ban, ChevronDown, ChevronUp } from 'lucide-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from 'recharts'
import type { SharedProps } from '@/types/shared'

interface DemandResponseProps extends SharedProps {
  demandSensitivity: string
  demandLeadTimeCurve: Record<string, number>
  maxDemandModifierPct: number | null
  maxRoomDemandPct: number | null
  // Room-Type Demand global kill switch (App::RateEngine::Config). When false, the stage is
  // off in the pipeline and its "Room maximum demand adjustment" control is hidden here.
  roomDemandEnabled: boolean
  maxTargetPressurePct: number | null
  activeEvents: Array<{
    name: string
    startDate: string
    endDate: string
    multiplier: number
  }>
}

// Mirrors backend SENSITIVITY_PRESETS: [pace_ratio, modifier_%]
// pace_ratio: 0 = no bookings, 1 = on-pace, 2 = 2x expected, 3 = 3x
const SENSITIVITY_CURVES: Record<string, Array<[number, number]>> = {
  none: [[0, 0], [0.3, 0], [0.5, 0], [0.7, 0], [1, 0], [1.3, 0], [1.5, 0], [2, 0], [3, 0], [4, 0]],
  conservative: [[0, -12], [0.3, -8], [0.5, -5], [0.7, -3], [1, 0], [1.3, 4], [1.5, 7], [2, 12], [3, 18], [4, 22]],
  balanced: [[0, -20], [0.3, -14], [0.5, -9], [0.7, -5], [1, 0], [1.3, 7], [1.5, 12], [2, 22], [3, 35], [4, 45]],
  aggressive: [[0, -30], [0.3, -22], [0.5, -15], [0.7, -8], [1, 0], [1.3, 10], [1.5, 18], [2, 35], [3, 55], [4, 70]],
}

function buildSparklineData(key: string) {
  const curve = SENSITIVITY_CURVES[key] || SENSITIVITY_CURVES.balanced
  return curve.map(([ratio, mod]) => ({ ratio, mod }))
}

const SENSITIVITY_OPTIONS = [
  {
    value: 'none',
    label: 'Disabled',
    icon: Ban,
    detail: 'No demand-driven adjustments. Rates are set purely by base rates, seasons, day-of-week, and lead time. Use this if you want full manual control or are just getting started.',
  },
  {
    value: 'conservative',
    label: 'Conservative',
    icon: Shield,
    detail: 'Rates respond gently to occupancy shifts. Best for price-sensitive markets or properties with lots of nearby competition. You\'ll capture modest revenue uplift during busy periods while maintaining rate stability. Typical adjustments: \u00b13\u201322%.',
  },
  {
    value: 'balanced',
    label: 'Balanced',
    icon: Scale,
    detail: 'The recommended starting point. Rates firm up meaningfully when demand is strong and soften enough to chase bookings when things are quiet. Works well for most properties. Typical adjustments: \u00b15\u201345%.',
  },
  {
    value: 'aggressive',
    label: 'Aggressive',
    icon: Zap,
    detail: 'Rates move sharply in both directions \u2014 maximising revenue during demand spikes and dropping quickly to fill rooms during slow periods. Best for unique properties with limited competition or strong seasonal swings. Typical adjustments: \u00b18\u201370%.',
  },
]

const DEMAND_LEAD_TIME_BUCKETS = [
  { days: 60, label: '60+ days', description: 'Far out — demand signals are weak. A low multiplier dampens adjustments, keeping rates stable.' },
  { days: 30, label: '30 days', description: 'Booking window opening. Moderate amplification lets demand start influencing rates.' },
  { days: 14, label: '14 days', description: 'Two weeks out — demand signals become reliable. Neutral (1.0x) passes demand through unchanged.' },
  { days: 7, label: '7 days', description: 'One week out — unsold rooms matter more. Amplify demand to capture late booking value.' },
  { days: 3, label: '3 days', description: 'Imminent stays. Strong amplification maximises yield on remaining inventory.' },
  { days: 1, label: '1 day', description: 'Same-day / next-day. Highest urgency — demand adjustments should be at their most responsive.' },
  { days: 0, label: 'Today', description: 'Check-in day. Maximum amplification for any last-second demand signals.' },
]

export default function DemandResponse({
  currentUser,
  property,
  properties,
  demandSensitivity: initialSensitivity,
  demandLeadTimeCurve: initialCurve,
  maxDemandModifierPct: initialMaxDemandModifierPct,
  maxRoomDemandPct: initialMaxRoomDemandPct,
  roomDemandEnabled,
  maxTargetPressurePct: initialMaxTargetPressurePct,
  activeEvents,
}: DemandResponseProps) {
  const base = property.basePath
  const sym = currencySymbol(property.currency)

  const [sensitivity, setSensitivity] = useState(initialSensitivity)

  const CURVE_DEFAULTS: Record<string, string> = { '0': '1.8', '1': '1.8', '3': '1.6', '7': '1.3', '14': '1.0', '30': '0.7', '60': '0.5' }

  const [demandLeadTimeCurve, setDemandLeadTimeCurve] = useState<Record<string, string>>(() => {
    if (Object.keys(initialCurve).length > 0) {
      return Object.fromEntries(Object.entries(initialCurve).map(([k, v]) => [k, v.toString()]))
    }
    return { ...CURVE_DEFAULTS }
  })

  const [maxDemandModifierPct, setMaxDemandModifierPct] = useState<string>(
    (initialMaxDemandModifierPct ?? 60).toString()
  )
  const [maxRoomDemandPct, setMaxRoomDemandPct] = useState<string>(
    (initialMaxRoomDemandPct ?? 20).toString()
  )
  const [maxTargetPressurePct, setMaxTargetPressurePct] = useState<string>(
    (initialMaxTargetPressurePct ?? 60).toString()
  )
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const [saving, setSaving] = useState(false)
  const review = usePricingReview()

  const isDirty = useMemo(() => {
    if (sensitivity !== initialSensitivity) return true
    if (maxDemandModifierPct !== (initialMaxDemandModifierPct ?? 60).toString()) return true
    if (maxRoomDemandPct !== (initialMaxRoomDemandPct ?? 20).toString()) return true
    if (maxTargetPressurePct !== (initialMaxTargetPressurePct ?? 60).toString()) return true
    const initialCurveEntries = Object.entries(initialCurve)
    const currentCurveEntries = Object.entries(demandLeadTimeCurve)
    if (currentCurveEntries.length !== initialCurveEntries.length) return true
    for (const [k, v] of initialCurveEntries) {
      if (demandLeadTimeCurve[k] !== v.toString()) return true
    }
    return false
  }, [sensitivity, initialSensitivity, maxDemandModifierPct, initialMaxDemandModifierPct, maxRoomDemandPct, initialMaxRoomDemandPct, maxTargetPressurePct, initialMaxTargetPressurePct, demandLeadTimeCurve, initialCurve])

  // Interpolate the demand lead time curve for the chart
  const curveChartData = useMemo(() => {
    const entries = Object.entries(demandLeadTimeCurve)
      .map(([k, v]) => ({ days: Number(k), multiplier: Number(v) }))
      .sort((a, b) => a.days - b.days)
    const points: Array<{ day: number; multiplier: number }> = []
    for (let d = 0; d <= 60; d++) {
      const lower = entries.filter(e => e.days <= d).pop()
      const upper = entries.find(e => e.days >= d)
      if (!lower || !upper) {
        points.push({ day: d, multiplier: lower?.multiplier ?? upper?.multiplier ?? 1.0 })
      } else if (lower.days === upper.days) {
        points.push({ day: d, multiplier: lower.multiplier })
      } else {
        const t = (d - lower.days) / (upper.days - lower.days)
        points.push({ day: d, multiplier: Math.round((lower.multiplier + t * (upper.multiplier - lower.multiplier)) * 100) / 100 })
      }
    }
    return points
  }, [demandLeadTimeCurve])

  // Reviewed on the server first (change safety R1.5), then sent by the same PATCH.
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const payload = {
      demand_sensitivity: sensitivity,
      demand_lead_time_curve: Object.fromEntries(
        Object.entries(demandLeadTimeCurve).map(([k, v]) => [k, Number(v)])
      ),
      max_demand_modifier_pct: Number(maxDemandModifierPct) || 60,
      max_target_pressure_pct: Number(maxTargetPressurePct) || 60,
      // Room-Type Demand is globally kill-switched — only send its ceiling when the stage is
      // enabled, so a disabled fleet never rewrites the max_room_demand_pct column.
      ...(roomDemandEnabled ? { max_room_demand_pct: Number(maxRoomDemandPct) || 20 } : {}),
    }
    review.open(`${base}/pricing-rules/demand-response/review`, payload, () =>
      inertiaSave('patch', `${base}/pricing-rules/demand-response`, payload, {
        onStart: () => setSaving(true),
        onFinish: () => setSaving(false),
      })
    )
  }

  return (
    <OperatorLayout title="Demand Response" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        <PricingRulesNav
          propertyBasePath={property.basePath}
          activeTab="demand-response"
          help={{
            content:
              'Controls how the rate engine reacts to booking pace, occupancy signals, and how far ahead the stay date is. These two settings work together: sensitivity sets overall aggressiveness, lead time modifiers fine-tune rates by booking window.',
            learnMore: '/guides/demand-response',
          }}
        />

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Demand Sensitivity */}
          <div className="animate-slide-in rounded-xl border border-border/60 bg-white p-6 shadow-sm" style={{ animationDelay: '100ms' }}>
            <h3 className="text-sm font-semibold text-foreground mb-1">Demand Sensitivity</h3>
            <p className="text-xs text-muted-foreground mb-5">
              How aggressively should rates respond to occupancy levels and booking pace? This controls the magnitude of demand-driven adjustments applied on top of your base rates and seasonal rules. When a date's booking-curve history is thin or volatile, the engine automatically damps the demand adjustment toward neutral so unreliable signals don't swing your rates.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {SENSITIVITY_OPTIONS.map((opt) => {
                const Icon = opt.icon
                const isActive = sensitivity === opt.value
                const sparkData = buildSparklineData(opt.value)
                const isDisabled = opt.value === 'none'
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSensitivity(opt.value)}
                    className={cn(
                      'rounded-xl border p-4 text-left transition-all cursor-pointer',
                      isActive
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                        : 'border-border/60 hover:border-border hover:bg-muted/30'
                    )}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={cn(
                        'flex h-7 w-7 items-center justify-center rounded-lg',
                        isActive ? 'bg-primary/10' : 'bg-muted'
                      )}>
                        <Icon className={cn('h-3.5 w-3.5', isActive ? 'text-primary' : 'text-muted-foreground')} />
                      </div>
                      <p className={cn(
                        'text-sm font-semibold',
                        isActive ? 'text-primary' : 'text-foreground'
                      )}>
                        {opt.label}
                      </p>
                    </div>
                    <p className="text-xs leading-relaxed text-muted-foreground mb-3">{opt.detail}</p>

                    {/* Sparkline: pace ratio → modifier % */}
                    <div className="rounded-lg bg-muted/30 px-2 pt-2 pb-1">
                      <ResponsiveContainer width="100%" height={60}>
                        <LineChart data={sparkData} margin={{ top: 2, right: 4, left: 4, bottom: 0 }}>
                          <ReferenceLine y={0} stroke="#cbd5e1" strokeWidth={1} />
                          <XAxis dataKey="ratio" hide />
                          <YAxis hide domain={[-35, 75]} />
                          <Line
                            type="monotone"
                            dataKey="mod"
                            stroke={isDisabled ? '#94a3b8' : isActive ? '#0d9488' : '#94a3b8'}
                            strokeWidth={isActive ? 2.5 : 1.5}
                            dot={false}
                            strokeDasharray={isDisabled ? '4 3' : undefined}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                      <div className="flex justify-between text-[9px] text-muted-foreground/60 px-1">
                        <span>Low demand</span>
                        <span>On pace</span>
                        <span>High demand</span>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>

            <p className="mt-2 text-[11px] text-muted-foreground/70">
              During events, pace is measured against event-adjusted expectations — a 1.0x pace means bookings match your event prediction.
            </p>

            {/* Advanced Demand Settings */}
            {sensitivity !== 'none' && (
              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => setAdvancedOpen(!advancedOpen)}
                  className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Advanced demand settings
                  {advancedOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>

                {advancedOpen && (
                  <div className="mt-4 space-y-5">
                    {/* Property Maximum Demand Adjustment */}
                    <div className="rounded-lg border border-border/40 bg-muted/10 p-4">
                      <label className="text-sm font-semibold text-foreground">Property maximum demand adjustment</label>
                      <p className="mt-0.5 text-xs text-muted-foreground mb-3">
                        The most the property-wide demand signal alone can move your rate, up or down. Applied after sensitivity and the lead-time curve. Other stages (seasons, day-of-week, events) are not affected by this limit.
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            max="100"
                            className="w-24 text-center rounded-lg text-sm font-semibold pr-7"
                            value={maxDemandModifierPct}
                            onChange={(e) => setMaxDemandModifierPct(e.target.value)}
                          />
                          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        At &plusmn;{Number(maxDemandModifierPct) || 60}%, a {sym}100 base rate stays between{' '}
                        {sym}{100 - (Number(maxDemandModifierPct) || 60)} and {sym}{100 + (Number(maxDemandModifierPct) || 60)} from property demand alone
                      </p>
                    </div>

                    {/* Room Maximum Demand Adjustment — hidden while the Room-Type Demand
                        stage is globally kill-switched (App::RateEngine::Config). */}
                    {roomDemandEnabled && (
                    <div className="rounded-lg border border-border/40 bg-muted/10 p-4">
                      <label className="text-sm font-semibold text-foreground">Room maximum demand adjustment</label>
                      <p className="mt-0.5 text-xs text-muted-foreground mb-3">
                        The most an individual room type can move up or down relative to the property when its own occupancy diverges from the property average. Keeps a single hot or cold room from drifting too far. Stacks on top of the property adjustment above.
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            max="100"
                            className="w-24 text-center rounded-lg text-sm font-semibold pr-7"
                            value={maxRoomDemandPct}
                            onChange={(e) => setMaxRoomDemandPct(e.target.value)}
                          />
                          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        At &plusmn;{Number(maxRoomDemandPct) || 20}%, a room can diverge up to {Number(maxRoomDemandPct) || 20}% from the property-wide demand move on its own
                      </p>
                    </div>
                    )}

                    {/* Target Pressure Maximum Adjustment */}
                    <div className="rounded-lg border border-border/40 bg-muted/10 p-4">
                      <label className="text-sm font-semibold text-foreground">Target pressure maximum adjustment</label>
                      <p className="mt-0.5 text-xs text-muted-foreground mb-3">
                        The most the Target Pressure stage alone can move a rate, up or down, when your forecast diverges from a saved occupancy target. Independent of the property demand limit above &mdash; raise it to let targets steer harder, lower it to keep target nudges gentle. The combined demand&nbsp;+&nbsp;target push is still bounded by the property maximum demand adjustment.
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            max="100"
                            className="w-24 text-center rounded-lg text-sm font-semibold pr-7"
                            value={maxTargetPressurePct}
                            onChange={(e) => setMaxTargetPressurePct(e.target.value)}
                          />
                          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        At &plusmn;{Number(maxTargetPressurePct) || 60}%, a {sym}100 rate stays between{' '}
                        {sym}{100 - (Number(maxTargetPressurePct) || 60)} and {sym}{100 + (Number(maxTargetPressurePct) || 60)} from target pressure alone
                      </p>
                    </div>

                    {/* Demand Lead Time Curve — moved from separate section, now under advanced */}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Event-Aware Demand Info Card */}
          {activeEvents.length > 0 && (
            <div className="animate-slide-in rounded-xl border border-border/60 border-l-4 border-l-primary bg-white p-5 shadow-sm" style={{ animationDelay: '100ms' }}>
              <h3 className="text-sm font-semibold text-foreground">
                Event-Aware Demand
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">
                When events are active, demand adjustments are measured against event-adjusted
                expectations rather than normal baselines. This prevents double-counting — your
                event multiplier sets the price target, demand fine-tunes around it based on
                actual bookings.
              </p>
              <div className="mt-3 space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">
                  {activeEvents.length} upcoming event{activeEvents.length !== 1 ? 's' : ''} affecting demand baselines:
                </p>
                {activeEvents.map((ev, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-foreground font-medium">{ev.name}</span>
                    <span className="text-muted-foreground tabular-nums">
                      {new Date(ev.startDate).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}
                      {' – '}
                      {new Date(ev.endDate).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}
                      {' · '}
                      <span className="font-semibold text-foreground">{ev.multiplier}x</span> expected demand
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <Link
                  href={`${base}/pricing-rules/events`}
                  className="text-xs font-medium text-primary hover:text-primary/80"
                >
                  Manage Events →
                </Link>
              </div>
            </div>
          )}

          {/* Demand Lead Time Curve */}
          <div className="animate-slide-in rounded-xl border border-border/60 bg-white p-6 shadow-sm" style={{ animationDelay: '150ms' }}>
            <h3 className="text-sm font-semibold text-foreground mb-1">Demand Lead Time Curve</h3>
            <p className="text-xs text-muted-foreground mb-5">
              Controls how strongly demand adjustments are amplified based on how far out the stay date is. A multiplier above 1.0x amplifies the demand signal (e.g., last-minute urgency), below 1.0x dampens it (e.g., far-future stability). At 1.0x, the demand signal passes through unchanged.
            </p>

            {/* Curve Chart */}
            <div className="mb-6 rounded-lg border border-border/40 bg-muted/20 p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Amplification Curve</span>
                <span className="text-[10px] text-muted-foreground">1.0x = demand signal unchanged</span>
              </div>
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={[...curveChartData].reverse()} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                    tickFormatter={(d) => `${d}d`}
                    reversed
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={false}
                    domain={[0.3, 2.2]}
                    tickFormatter={(v) => `${v}x`}
                    width={35}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      fontSize: 11,
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)',
                    }}
                    formatter={(value: number | undefined) => [`${value ?? 0}x`, 'Amplification']}
                    labelFormatter={(day) => `${day} days before check-in`}
                  />
                  <ReferenceLine y={1.0} stroke="#94a3b8" strokeDasharray="4 4" strokeWidth={1} />
                  <Line
                    type="monotone"
                    dataKey="multiplier"
                    stroke="#0d9488"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: '#0d9488', stroke: '#fff', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="space-y-2">
              {DEMAND_LEAD_TIME_BUCKETS.map((bucket) => {
                const val = Number(demandLeadTimeCurve[bucket.days.toString()]) || 1.0
                const isAmplified = val > 1.0
                const isDampened = val < 1.0
                return (
                  <div
                    key={bucket.days}
                    className={cn(
                      'flex items-center gap-4 rounded-lg border px-4 py-3 transition-all',
                      isAmplified && 'border-emerald-200 bg-emerald-50/30',
                      isDampened && 'border-amber-200 bg-amber-50/30',
                      !isAmplified && !isDampened && 'border-border/60'
                    )}
                  >
                    <div className="w-20 shrink-0">
                      <span className="text-sm font-semibold text-foreground">{bucket.label}</span>
                    </div>
                    <p className="flex-1 text-xs text-muted-foreground hidden sm:block">
                      {bucket.description}
                    </p>
                    <div className="relative shrink-0">
                      <Input
                        type="number"
                        step="0.1"
                        min="0.1"
                        max="3.0"
                        className="w-20 text-center rounded-lg text-sm font-semibold pr-5"
                        value={demandLeadTimeCurve[bucket.days.toString()] ?? '1.0'}
                        onChange={(e) => setDemandLeadTimeCurve((prev) => ({
                          ...prev,
                          [bucket.days.toString()]: e.target.value,
                        }))}
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">x</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={!isDirty || saving} className="rounded-lg px-6">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
      {review.dialog}
    </OperatorLayout>
  )
}
